import i18n from "@/shared/i18n";

const nf = new Map<string, Intl.NumberFormat>();

/** Formats an amount (decimal string from the API) with currency decimals. */
export function money(v: string | number | null | undefined, currency?: string, decimals?: number): string {
  if (v === null || v === undefined || v === "") return "—";
  const d = decimals ?? (currency && ["KWD", "BHD", "OMR"].includes(currency) ? 3 : 2);
  const key = `${d}`;
  let f = nf.get(key);
  if (!f) {
    f = new Intl.NumberFormat("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
    nf.set(key, f);
  }
  return f.format(Number(v));
}

export function num(v: string | number | null | undefined, max = 2): string {
  if (v === null || v === undefined || v === "") return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: max }).format(Number(v));
}

export function date(v: string | null | undefined): string {
  if (!v) return "—";
  const d = v.length === 10 ? new Date(v + "T00:00:00") : new Date(v);
  return new Intl.DateTimeFormat(i18n.language === "ar" ? "ar-EG-u-nu-latn" : "en-GB", { day: "numeric", month: "short", year: "numeric" }).format(d);
}

/** A month by name ("أغسطس 2026"), from any date in it (or "2026-08"). */
export function month(v: string | null | undefined): string {
  if (!v) return "—";
  return new Intl.DateTimeFormat(i18n.language === "ar" ? "ar-EG-u-nu-latn" : "en-GB", { month: "long", year: "numeric" }).format(new Date(v.slice(0, 7) + "-01T00:00:00"));
}

export function dateTime(v: string | null | undefined): string {
  if (!v) return "—";
  return new Intl.DateTimeFormat(i18n.language === "ar" ? "ar-EG-u-nu-latn" : "en-GB", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  }).format(new Date(v));
}

export function relative(v: string): string {
  const diff = (new Date(v).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86400), "day");
}

// The company's time zone, from its settings: its "today" and "this month" follow its
// clock, not the browser's.
let zone: string | undefined;
export function setTimeZone(z?: string) {
  zone = z;
}

/** Today's year, month (1–12) and day on the company's clock. */
export function todayParts(): { y: number; m: number; d: number } {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const part = (t: string) => Number(p.find((x) => x.type === t)?.value);
  return { y: part("year"), m: part("month"), d: part("day") };
}

/** A date as YYYY-MM-DD; days and months past their end run into the next (day 0 is the last of the month before). */
export function isoDate(y: number, m: number, d: number): string {
  return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
}

export function today(): string {
  const { y, m, d } = todayParts();
  return isoDate(y, m, d);
}

export function thisMonth(): string {
  return today().slice(0, 7);
}

export function thisYear(): number {
  return todayParts().y;
}

export function addDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function minutesToHours(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}:${String(mm).padStart(2, "0")}`;
}

/** 12.4 MB style size for a byte count. */
export function bytes(n: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${i === 0 ? n : n.toFixed(1)} ${units[i]}`;
}
